/**
 * Admin controls as the signed-in person sees them (architecture/33 §7).
 *
 * One request at sign-in, refreshed when the tab regains focus and once a
 * minute, so an admin's change reaches an open dashboard without a reload. A
 * screen that an admin can switch off reads its switch here
 * (`useControl("models.user_selection_enabled", true)`) instead of asking the
 * endpoint behind the feature. Without a provider, or before the first answer,
 * `useControl` returns the fallback: a control that cannot be read never hides
 * or breaks a screen.
 */
import * as React from "react";
import { myControls } from "@/api";
import { useApiClient } from "@/api/useApiClient";
import { useAuth } from "@/auth/useAuth";

type Values = Record<string, unknown>;

const ControlsContext = React.createContext<Values>({});
const REFRESH_MS = 60_000;

export function ControlsProvider({ children }: { children: React.ReactNode }) {
  const client = useApiClient();
  const { user } = useAuth();
  const signedIn = Boolean(user);
  const [values, setValues] = React.useState<Values>({});

  React.useEffect(() => {
    if (!signedIn) {
      setValues({});
      return;
    }
    let mounted = true;
    const load = () => {
      myControls({ client })
        .then(({ data }) => {
          if (!mounted || !data) return;
          setValues(Object.fromEntries(Object.entries(data.controls).map(([k, c]) => [k, c.value])));
        })
        .catch(() => {
          /* keep the last values: a failed refresh must not change what the person sees */
        });
    };
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    window.addEventListener("focus", load);
    return () => {
      mounted = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", load);
    };
  }, [client, signedIn]);

  return <ControlsContext.Provider value={values}>{children}</ControlsContext.Provider>;
}

export function useControl<T>(key: string, fallback: T): T {
  const values = React.useContext(ControlsContext);
  const value = values[key];
  return value === undefined || value === null ? fallback : (value as T);
}
